import { ReactNode } from 'react'
import { RaidPlanAssignmentEntity } from 'app/frontend/types/custom/RaidPlan'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import {
  getAllClasses,
  getClass,
  getSpecialization,
} from 'app/frontend/base/services/characterValues'
import { MAX_ENTITY_COUNT } from '../constants'
import { resolveCanvasClassColor } from '../canvasClassColor'
import { getEntityNumberStarts } from './entityNumbering'
import { isIconShape } from './iconShapes'

export type ResolvedEntityBadge = {
  name: string
  color?: string
  index: number
  label?: ReactNode
  isOwn?: boolean
  isOverridden?: boolean
  characterId?: number
}

export type VisualEntitySlot = {
  number: number
  shapeId: string
  localIndex: number
}

type SpecializationInfo = {
  name: string
  icon: string
  classId: number
  className: string
}

export type VisualEntityMatchMode = 'hint-first' | 'number-first'

type Input = {
  shapes: VisualShape[]
  entities: RaidPlanAssignmentEntity[]
  specializationById: Record<number, SpecializationInfo>
  memberInfoByCharacterId: Record<number, { name: string; className: string }>
  matchMode?: VisualEntityMatchMode
}

type RoleKey = 'tank' | 'healer' | 'melee' | 'ranged' | 'dps'

type Hint =
  | { type: 'spec'; specId: number; classId?: number }
  | { type: 'class'; classId: number }
  | { type: 'role'; role: RoleKey }

type Slot = {
  number: number
  hint?: Hint
}

type EntityDescriptor = {
  number: number
  entity: RaidPlanAssignmentEntity
  role?: RoleKey
  classId?: number
  specId?: number
}

const classIdByName = new Map(
  Object.entries(getAllClasses()).map(([id, name]) => [name, Number(id)]),
)

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1)

const normalizeRole = (value: string): RoleKey | undefined => {
  switch (value) {
    case 'tank':
    case 'tanks':
      return 'tank'
    case 'heal':
    case 'healer':
    case 'healers':
      return 'healer'
    case 'melee':
      return 'melee'
    case 'ranged':
      return 'ranged'
    case 'dps':
    case 'damage':
      return 'dps'
    default:
      return undefined
  }
}

const rolesCompatible = (slotRole: RoleKey, entityRole: RoleKey): boolean => {
  if (slotRole === entityRole) return true
  if (slotRole === 'melee' || slotRole === 'ranged') return entityRole === 'dps'
  if (slotRole === 'dps') {
    return entityRole === 'melee' || entityRole === 'ranged'
  }
  return false
}

const roleDisplayName = (role: RoleKey): string =>
  role === 'dps' ? 'DPS' : capitalize(role)

const shapeHint = (shape: VisualShape): Hint | undefined => {
  if (!isIconShape(shape)) return undefined
  if (shape.kind === 'spec') {
    const specId = Number(shape.value)
    if (!Number.isFinite(specId)) return undefined
    const spec = getSpecialization(shape.value)
    const classId =
      typeof spec?.class_id === 'number' ? spec.class_id : undefined
    return { type: 'spec', specId, classId }
  }
  if (shape.kind === 'class') {
    const classId = classIdByName.get(shape.value)
    if (classId === undefined) return undefined
    return { type: 'class', classId }
  }
  if (shape.kind === 'role') {
    const role = normalizeRole(shape.value)
    if (!role) return undefined
    return { type: 'role', role }
  }
  return undefined
}

export const countScreenEntitySlots = (shapes: VisualShape[]): number =>
  buildSlots(shapes).length

export const buildVisualEntitySlots = (
  shapes: VisualShape[],
): VisualEntitySlot[] => {
  const starts = getEntityNumberStarts(shapes)
  const slots: VisualEntitySlot[] = []
  for (const shape of shapes) {
    const start = starts.get(shape.id)
    if (start === undefined) continue
    const count = Math.min(shape.entity_count ?? 0, MAX_ENTITY_COUNT)
    for (let index = 0; index < count; index++) {
      slots.push({
        number: start + index,
        shapeId: shape.id,
        localIndex: index,
      })
    }
  }
  return slots
}

const buildSlots = (shapes: VisualShape[]): Slot[] => {
  const starts = getEntityNumberStarts(shapes)
  const slots: Slot[] = []
  for (const shape of shapes) {
    const start = starts.get(shape.id)
    if (start === undefined) continue
    const count = Math.min(shape.entity_count ?? 0, MAX_ENTITY_COUNT)
    const hint = shapeHint(shape)
    for (let index = 0; index < count; index++) {
      slots.push({ number: start + index, hint })
    }
  }
  return slots
}

const memberClassName = (
  entity: Extract<RaidPlanAssignmentEntity, { kind: 'member' }>,
  specializationById: Record<number, SpecializationInfo>,
  memberInfoByCharacterId: Record<number, { name: string; className: string }>,
): string | undefined => {
  const spec = entity.spec_id ? specializationById[entity.spec_id] : undefined
  return (
    spec?.className ?? memberInfoByCharacterId[entity.character_id]?.className
  )
}

const describeEntity = (
  entity: RaidPlanAssignmentEntity,
  number: number,
  specializationById: Record<number, SpecializationInfo>,
  memberInfoByCharacterId: Record<number, { name: string; className: string }>,
): EntityDescriptor => {
  if (entity.kind === 'member') {
    const spec = entity.spec_id ? specializationById[entity.spec_id] : undefined
    const className = memberClassName(
      entity,
      specializationById,
      memberInfoByCharacterId,
    )
    const classId =
      spec?.classId ?? (className ? classIdByName.get(className) : undefined)
    return {
      number,
      entity,
      role: normalizeRole(entity.role),
      classId,
      specId: entity.spec_id ?? undefined,
    }
  }
  if (entity.kind === 'by_class') {
    return { number, entity, classId: entity.class_id }
  }
  if (entity.kind === 'by_role') {
    return { number, entity, role: normalizeRole(entity.role) }
  }
  if (entity.kind === 'role') {
    return { number, entity, role: normalizeRole(entity.role) }
  }
  if (entity.kind === 'collection') {
    return { number, entity, role: normalizeRole(entity.collection) }
  }
  return { number, entity }
}

const badgeFor = (
  entity: RaidPlanAssignmentEntity,
  specializationById: Record<number, SpecializationInfo>,
  memberInfoByCharacterId: Record<number, { name: string; className: string }>,
): { name: string; color?: string } => {
  if (entity.kind === 'member') {
    const name =
      memberInfoByCharacterId[entity.character_id]?.name ?? entity.label
    const className = memberClassName(
      entity,
      specializationById,
      memberInfoByCharacterId,
    )
    return {
      name,
      color: className ? resolveCanvasClassColor(className) : undefined,
    }
  }
  if (entity.kind === 'by_class') {
    const className = getClass(entity.class_id.toString())
    return {
      name: className ?? 'Unknown',
      color: className ? resolveCanvasClassColor(className) : undefined,
    }
  }
  if (entity.kind === 'by_role') {
    if (entity.role === 'anyone') return { name: 'Any' }
    const role = normalizeRole(entity.role)
    return { name: role ? roleDisplayName(role) : capitalize(entity.role) }
  }
  if (entity.kind === 'role') {
    const role = normalizeRole(entity.role)
    return { name: role ? roleDisplayName(role) : capitalize(entity.role) }
  }
  if (entity.kind === 'collection') {
    return { name: capitalize(entity.collection) }
  }
  if (entity.kind === 'group') {
    return { name: `Group ${entity.group}` }
  }
  return { name: 'Everyone' }
}

export const createResolvedEntityBadge = (
  entity: RaidPlanAssignmentEntity,
  index: number,
  specializationById: Record<number, SpecializationInfo>,
  memberInfoByCharacterId: Record<number, { name: string; className: string }>,
  extras?: { isOverridden?: boolean },
): ResolvedEntityBadge => ({
  ...badgeFor(entity, specializationById, memberInfoByCharacterId),
  index,
  characterId: entity.kind === 'member' ? entity.character_id : undefined,
  isOverridden: extras?.isOverridden,
})

const scoreFor = (hint: Hint, descriptor: EntityDescriptor): number => {
  if (hint.type === 'spec') {
    if (descriptor.specId !== undefined && descriptor.specId === hint.specId) {
      return 3
    }
    if (hint.classId !== undefined && descriptor.classId === hint.classId) {
      return 2
    }
    return 0
  }
  if (hint.type === 'class') {
    return descriptor.classId === hint.classId ? 2 : 0
  }
  if (descriptor.role && rolesCompatible(hint.role, descriptor.role)) return 1
  return 0
}

const HINT_SPECIFICITY: Record<Hint['type'], number> = {
  spec: 0,
  class: 1,
  role: 2,
}

const slotAccepts = (slot: Slot, descriptor: EntityDescriptor): boolean => {
  if (!slot.hint) return true
  if (scoreFor(slot.hint, descriptor) > 0) return true
  return !descriptor.role
}

export const resolveVisualPlanEntityAssignments = ({
  shapes,
  entities,
  specializationById,
  memberInfoByCharacterId,
  matchMode = 'hint-first',
}: Input): Map<number, ResolvedEntityBadge> => {
  const slots = buildSlots(shapes)
  const descriptors = entities.map((entity, index) =>
    describeEntity(
      entity,
      index + 1,
      specializationById,
      memberInfoByCharacterId,
    ),
  )

  const claimed = new Set<number>()
  const assignment = new Map<number, EntityDescriptor>()

  const assignExactNumbers = (targetSlots: Slot[]) => {
    for (const slot of targetSlots) {
      if (assignment.has(slot.number)) continue
      const exact = descriptors.find(
        (descriptor) =>
          descriptor.number === slot.number && !claimed.has(descriptor.number),
      )
      if (!exact || !slotAccepts(slot, exact)) continue
      claimed.add(exact.number)
      assignment.set(slot.number, exact)
    }
  }

  if (matchMode === 'number-first') {
    assignExactNumbers(slots.filter((slot) => slot.hint === undefined))
    assignExactNumbers(slots.filter((slot) => slot.hint !== undefined))
  }

  const semanticSlots = slots
    .filter((slot): slot is Slot & { hint: Hint } => slot.hint !== undefined)
    .sort(
      (left, right) =>
        HINT_SPECIFICITY[left.hint.type] - HINT_SPECIFICITY[right.hint.type],
    )

  for (const slot of semanticSlots) {
    if (assignment.has(slot.number)) continue
    let best: EntityDescriptor | undefined
    let bestScore = 0
    let bestDistance = Infinity
    for (const descriptor of descriptors) {
      if (claimed.has(descriptor.number)) continue
      const score = scoreFor(slot.hint, descriptor)
      if (score === 0) continue
      const distance = Math.abs(descriptor.number - slot.number)
      if (
        score > bestScore ||
        (score === bestScore && distance < bestDistance)
      ) {
        best = descriptor
        bestScore = score
        bestDistance = distance
      }
    }
    if (best) {
      claimed.add(best.number)
      assignment.set(slot.number, best)
    }
  }

  const fillByNumber = (targetSlots: Slot[]) => {
    const pending = targetSlots
      .filter((slot) => !assignment.has(slot.number))
      .sort((left, right) => left.number - right.number)

    for (const slot of pending) {
      if (assignment.has(slot.number)) continue
      const exact = descriptors.find(
        (descriptor) =>
          descriptor.number === slot.number &&
          !claimed.has(descriptor.number) &&
          slotAccepts(slot, descriptor),
      )
      if (exact) {
        claimed.add(exact.number)
        assignment.set(slot.number, exact)
      }
    }

    for (const slot of pending) {
      if (assignment.has(slot.number)) continue
      let best: EntityDescriptor | undefined
      let bestDistance = Infinity
      for (const descriptor of descriptors) {
        if (claimed.has(descriptor.number)) continue
        if (!slotAccepts(slot, descriptor)) continue
        const distance = Math.abs(descriptor.number - slot.number)
        if (distance < bestDistance) {
          best = descriptor
          bestDistance = distance
        }
      }
      if (best) {
        claimed.add(best.number)
        assignment.set(slot.number, best)
      }
    }
  }

  const remainingSlots = slots.filter((slot) => !assignment.has(slot.number))
  fillByNumber(remainingSlots.filter((slot) => slot.hint === undefined))
  fillByNumber(remainingSlots.filter((slot) => slot.hint !== undefined))

  const result = new Map<number, ResolvedEntityBadge>()
  assignment.forEach((descriptor, number) => {
    result.set(
      number,
      createResolvedEntityBadge(
        descriptor.entity,
        descriptor.number - 1,
        specializationById,
        memberInfoByCharacterId,
      ),
    )
  })
  return result
}

type FillEmptyVisualPlanSlotsInput = Input & {
  assignments: Map<number, ResolvedEntityBadge>
  skipSlotNumbers?: Set<number>
}

export const fillEmptyVisualPlanSlots = ({
  shapes,
  entities,
  specializationById,
  memberInfoByCharacterId,
  assignments,
  skipSlotNumbers,
}: FillEmptyVisualPlanSlotsInput): Map<number, ResolvedEntityBadge> => {
  const slots = buildSlots(shapes)
  const descriptors = entities.map((entity, index) =>
    describeEntity(
      entity,
      index + 1,
      specializationById,
      memberInfoByCharacterId,
    ),
  )
  const claimed = new Set<number>()
  assignments.forEach((badge) => {
    if (badge.characterId != null) {
      const descriptor = descriptors.find(
        (entry) =>
          entry.entity.kind === 'member' &&
          entry.entity.character_id === badge.characterId,
      )
      if (descriptor) claimed.add(descriptor.number)
      return
    }
    if (badge.index >= 0) claimed.add(badge.index + 1)
  })

  const assignment = new Map<number, EntityDescriptor>()
  const emptySlots = slots.filter(
    (slot) =>
      !assignments.has(slot.number) && !skipSlotNumbers?.has(slot.number),
  )

  const semanticSlots = emptySlots
    .filter((slot): slot is Slot & { hint: Hint } => slot.hint !== undefined)
    .sort(
      (left, right) =>
        HINT_SPECIFICITY[left.hint.type] - HINT_SPECIFICITY[right.hint.type],
    )

  for (const slot of semanticSlots) {
    let best: EntityDescriptor | undefined
    let bestScore = 0
    let bestDistance = Infinity
    for (const descriptor of descriptors) {
      if (claimed.has(descriptor.number)) continue
      const score = scoreFor(slot.hint, descriptor)
      if (score === 0) continue
      const distance = Math.abs(descriptor.number - slot.number)
      if (
        score > bestScore ||
        (score === bestScore && distance < bestDistance)
      ) {
        best = descriptor
        bestScore = score
        bestDistance = distance
      }
    }
    if (best) {
      claimed.add(best.number)
      assignment.set(slot.number, best)
    }
  }

  const fillByNumber = (targetSlots: Slot[]) => {
    const pending = targetSlots
      .filter((slot) => !assignment.has(slot.number))
      .sort((left, right) => left.number - right.number)

    for (const slot of pending) {
      if (assignment.has(slot.number)) continue
      const exact = descriptors.find(
        (descriptor) =>
          descriptor.number === slot.number && !claimed.has(descriptor.number),
      )
      if (exact) {
        claimed.add(exact.number)
        assignment.set(slot.number, exact)
      }
    }

    for (const slot of pending) {
      if (assignment.has(slot.number)) continue
      let best: EntityDescriptor | undefined
      let bestDistance = Infinity
      for (const descriptor of descriptors) {
        if (claimed.has(descriptor.number)) continue
        const distance = Math.abs(descriptor.number - slot.number)
        if (distance < bestDistance) {
          best = descriptor
          bestDistance = distance
        }
      }
      if (best) {
        claimed.add(best.number)
        assignment.set(slot.number, best)
      }
    }
  }

  const remainingSlots = emptySlots.filter(
    (slot) => !assignment.has(slot.number),
  )
  fillByNumber(remainingSlots.filter((slot) => slot.hint === undefined))
  fillByNumber(remainingSlots.filter((slot) => slot.hint !== undefined))

  const result = new Map(assignments)
  assignment.forEach((descriptor, number) => {
    result.set(
      number,
      createResolvedEntityBadge(
        descriptor.entity,
        descriptor.number - 1,
        specializationById,
        memberInfoByCharacterId,
      ),
    )
  })
  return result
}
